import { useState, useEffect, FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { useScrollAnimation } from '../../hooks/useScrollAnimation';

export default function ContactPage() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Handle scroll for navbar
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsSubmitting(true);
    setFormStatus('idle');

    const form = e.currentTarget;
    const formData = new FormData(form);

    // Validate textarea length
    const descripcion = formData.get('descripcion_experiencia') as string;
    if (descripcion && descripcion.length > 500) {
      setFormStatus('error');
      setIsSubmitting(false);
      return;
    }

    try {
      const response = await fetch('https://readdy.ai/api/form/d4u82jnsdr6u9jriffj0', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams(formData as any).toString(),
      });

      if (response.ok) {
        setFormStatus('success');
        form.reset();
      } else {
        setFormStatus('error');
      }
    } catch (error) {
      setFormStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Animation hooks
  const heroAnimation = useScrollAnimation({ threshold: 0.2 });
  const infoAnimation = useScrollAnimation({ threshold: 0.2 });
  const formAnimation = useScrollAnimation({ threshold: 0.2 });
  const germanPhotoAnimation = useScrollAnimation({ threshold: 0.3 });
  const technicianPhotoAnimation = useScrollAnimation({ threshold: 0.3 });
  const happyClientsAnimation = useScrollAnimation({ threshold: 0.3 });
  const whatsappAnimation = useScrollAnimation({ threshold: 0.3 });
  const mapAnimation = useScrollAnimation({ threshold: 0.3 });

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className={`fixed w-full z-50 transition-all duration-500 ${isScrolled ? 'bg-white shadow-md' : 'bg-transparent'}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-20">
            <Link to="/" className="flex items-center space-x-3 cursor-pointer">
              {/* Logo actualizado */}
              <img 
                src="https://static.readdy.ai/image/e5ec1a966ead4ea4405a383d967bb80c/5b74a69037f50dd7137b29dc6f7b5981.png"
                alt="HogarSoluciones Ecuador Logo"
                className="h-16 w-auto"
              />
            </Link>
            
            <div className="hidden md:flex items-center space-x-8">
              <Link to="/" className={`text-lg font-medium transition-colors cursor-pointer ${isScrolled ? 'text-gray-700 hover:text-teal-600' : 'text-white hover:text-teal-200'}`}>
                Inicio
              </Link>
              <a href="/#servicios" className={`text-lg font-medium transition-colors cursor-pointer ${isScrolled ? 'text-gray-700 hover:text-teal-600' : 'text-white hover:text-teal-200'}`}>
                Servicios
              </a>
              <a href="/#como-funciona" className={`text-lg font-medium transition-colors cursor-pointer ${isScrolled ? 'text-gray-700 hover:text-teal-600' : 'text-white hover:text-teal-200'}`}>
                Cómo Funciona
              </a>
              <Link to="/contacto" className={`text-lg font-medium transition-colors cursor-pointer ${isScrolled ? 'text-teal-600' : 'text-white'}`}>
                Únete como Técnico
              </Link>
              <a href="/#solicitar" className="bg-teal-600 text-white px-6 py-3 rounded-lg text-base font-semibold hover:bg-teal-700 whitespace-nowrap cursor-pointer">
                Solicitar Servicio
              </a>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section ref={heroAnimation.elementRef} className="relative pt-32 pb-20 bg-gradient-to-br from-teal-600 via-teal-500 to-cyan-500 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-black/10 to-black/20"></div>
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 left-10 w-72 h-72 bg-white rounded-full blur-3xl animate-pulse"></div>
          <div className="absolute bottom-20 right-10 w-96 h-96 bg-white rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }}></div>
        </div>
        
        <div className={`relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center transition-all duration-1000 ${
          heroAnimation.isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
        }`}>
          <h1 className="text-5xl md:text-6xl font-bold text-white mb-6 leading-tight animate-fade-in-up">
            ¿Eres un Técnico Profesional?
          </h1>
          <p className="text-xl md:text-2xl text-white/95 mb-4 max-w-4xl mx-auto leading-relaxed animate-fade-in-up" style={{ animationDelay: '0.2s' }}>
            Únete a nuestra red de profesionales verificados
          </p>
          <p className="text-lg text-white/90 max-w-3xl mx-auto leading-relaxed mb-8 animate-fade-in-up" style={{ animationDelay: '0.4s' }}>
            Conectamos técnicos calificados con clientes que necesitan servicios de calidad en todo Ecuador
          </p>
          
          {/* Mensaje Destacado - Anticípanos el Comienzo */}
          <div className={`bg-gradient-to-r from-yellow-400 via-yellow-300 to-yellow-400 rounded-2xl p-6 max-w-4xl mx-auto shadow-2xl border-4 border-white mb-6 transition-all duration-1000 delay-100 hover:scale-105 animate-bounce ${
            heroAnimation.isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
          }`}>
            <div className="flex items-center justify-center space-x-4">
              <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center animate-pulse">
                <i className="ri-calendar-check-line text-4xl text-teal-600"></i>
              </div>
              <div className="text-center">
                <h3 className="text-3xl lg:text-4xl font-black text-gray-900 drop-shadow-lg">
                  ¡ANTICÍPANOS EL COMIENZO PAUTADO DE LA OBRA REQUERIDA!
                </h3>
              </div>
            </div>
          </div>
          
          {/* Mensaje de Respuesta Inmediata */}
          <div className={`bg-gradient-to-r from-yellow-400 to-yellow-300 rounded-2xl p-6 max-w-4xl mx-auto shadow-2xl border-4 border-white transition-all duration-1000 delay-200 hover:scale-105 animate-scale-in ${
            heroAnimation.isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
          }`}>
            <div className="flex items-center justify-center space-x-4">
              <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center animate-pulse">
                <i className="ri-customer-service-2-line text-4xl text-teal-600"></i>
              </div>
              <div className="text-left">
                <h3 className="text-2xl lg:text-3xl font-bold text-gray-900 mb-1">
                  ¡RESPONDEMOS INMEDIATAMENTE!
                </h3>
                <p className="text-lg lg:text-xl font-semibold text-gray-800">
                  Fuera del horario de oficinas por chat de voz y texto 24/7
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
            
            {/* Left Column - Information */}
            <div ref={infoAnimation.elementRef} className="space-y-8">
              <div className={`transition-all duration-1000 ${
                infoAnimation.isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-10'
              }`}>
                <h2 className="text-4xl font-bold text-gray-900 mb-6 animate-slide-in-left">
                  Solicitud Formal para Técnicos Profesionales Experimentados
                </h2>
                <p className="text-lg text-gray-700 leading-relaxed mb-6 animate-slide-in-left" style={{ animationDelay: '0.1s' }}>
                  En <strong>HogarSoluciones Ecuador</strong> estamos construyendo una red de profesionales de confianza para satisfacer la creciente demanda de servicios de calidad en hogares, oficinas y locales comerciales.
                </p>
              </div>

              <div className={`bg-white rounded-xl p-8 shadow-lg border-l-4 border-teal-600 hover:shadow-2xl hover:scale-105 transition-all duration-500 delay-200 animate-slide-in-left ${
                infoAnimation.isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-10'
              }`}>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">
                  ¿Por qué unirte a nosotros?
                </h3>
                <ul className="space-y-4">
                  {[
                    { title: 'Flujo constante de clientes:', desc: 'Accede a proyectos continuos en toda la región' },
                    { title: 'Gestión profesional:', desc: 'Nosotros coordinamos, tú te enfocas en tu trabajo' },
                    { title: 'Pagos garantizados:', desc: 'Sistema transparente y confiable' },
                    { title: 'Crecimiento profesional:', desc: 'Construye tu reputación con reseñas verificadas' }
                  ].map((item, index) => (
                    <li key={index} className="flex items-start group animate-fade-in-up" style={{ animationDelay: `${index * 0.1}s` }}>
                      <i className="ri-check-double-line text-2xl text-teal-600 mr-3 mt-1 group-hover:scale-125 transition-transform duration-300"></i>
                      <div>
                        <strong className="text-gray-900">{item.title}</strong>
                        <p className="text-gray-700">{item.desc}</p>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>

              <div className={`bg-teal-600 rounded-xl p-8 text-white shadow-lg hover:shadow-2xl hover:scale-105 transition-all duration-500 delay-400 animate-slide-in-left ${
                infoAnimation.isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-10'
              }`}>
                <h3 className="text-2xl font-bold mb-4">
                  Servicios que Necesitamos
                </h3>
                <div className="grid grid-cols-2 gap-4">
                  {[
                    { icon: 'ri-tools-line', name: 'Albañilería' },
                    { icon: 'ri-drop-line', name: 'Plomería' },
                    { icon: 'ri-key-line', name: 'Cerrajería' },
                    { icon: 'ri-paint-brush-line', name: 'Pintura' },
                    { icon: 'ri-flashlight-line', name: 'Electricidad' },
                    { icon: 'ri-temp-cold-line', name: 'Refrigeración' },
                    { icon: 'ri-plant-line', name: 'Jardinería' },
                    { icon: 'ri-hammer-line', name: 'Reparaciones' }
                  ].map((service, index) => (
                    <div key={index} className="flex items-center group animate-fade-in-up" style={{ animationDelay: `${index * 0.1}s` }}>
                      <i className={`${service.icon} text-2xl mr-3 group-hover:scale-125 group-hover:rotate-12 transition-all duration-300`}></i>
                      <span>{service.name}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Nota Importante sobre Acuerdo Contractual */}
              <div className={`bg-gradient-to-r from-amber-50 to-yellow-50 border-l-4 border-amber-500 rounded-xl p-8 shadow-lg hover:shadow-xl transition-all duration-500 delay-500 animate-slide-in-left ${
                infoAnimation.isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-10'
              }`}>
                <div className="flex items-start space-x-4">
                  <div className="flex-shrink-0">
                    <div className="w-12 h-12 bg-amber-500 rounded-full flex items-center justify-center animate-pulse">
                      <i className="ri-file-text-line text-2xl text-white"></i>
                    </div>
                  </div>
                  <div className="flex-1">
                    <h4 className="text-xl font-bold text-gray-900 mb-3 flex items-center">
                      <i className="ri-information-line text-amber-600 mr-2"></i>
                      NOTA IMPORTANTE PARA TÉCNICOS
                    </h4>
                    <p className="text-gray-800 leading-relaxed mb-3">
                      <strong>Todos los trabajos se formalizan mediante un acuerdo contractual</strong> que protege los intereses de todas las partes: técnico profesional, cliente y HogarSoluciones Ecuador.
                    </p>
                    <p className="text-gray-700 leading-relaxed text-sm">
                      Este documento incluirá: tipo de servicio específico, costo acordado, condiciones de trabajo, plazos de ejecución, forma de pago y garantías. El acuerdo será definido y firmado antes de iniciar cualquier trabajo, asegurando transparencia, profesionalismo y protección legal para todos.
                    </p>
                  </div>
                </div>
              </div>

              {/* Foto de Germán Vallejos con Marco Bonito */}
              <div ref={germanPhotoAnimation.elementRef} className={`transition-all duration-1000 delay-600 ${
                germanPhotoAnimation.isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-10'
              }`}>
                <div className="relative group">
                  {/* Marco decorativo exterior */}
                  <div className="absolute -inset-4 bg-gradient-to-r from-teal-600 via-cyan-500 to-teal-600 rounded-2xl opacity-75 group-hover:opacity-100 blur-lg group-hover:blur-xl transition-all duration-500 animate-pulse-slow"></div>
                  
                  {/* Contenedor principal */}
                  <div className="relative bg-white rounded-2xl p-6 shadow-2xl hover:shadow-3xl transition-all duration-500 transform group-hover:scale-105">
                    {/* Marco interior dorado */}
                    <div className="relative border-4 border-gradient-to-r from-yellow-400 via-yellow-500 to-yellow-400 rounded-xl overflow-hidden shadow-xl">
                      <img 
                        src="https://static.readdy.ai/image/e5ec1a966ead4ea4405a383d967bb80c/37a61535e51c345f0a1c31584e2997fa.jpeg"
                        alt="Germán Vallejos - Fundador de HogarSoluciones Ecuador"
                        className="w-full h-96 object-cover object-center group-hover:scale-110 transition-transform duration-700"
                      />
                      {/* Overlay con degradado */}
                      <div className="absolute inset-0 bg-gradient-to-t from-teal-900/80 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                    </div>
                    
                    {/* Información debajo de la foto */}
                    <div className="mt-6 text-center">
                      <h3 className="text-3xl font-bold text-gray-900 mb-2 animate-fade-in-up">
                        Germán Vallejos
                      </h3>
                      <p className="text-xl font-semibold text-teal-600 mb-3 animate-fade-in-up" style={{ animationDelay: '0.1s' }}>
                        Fundador de HogarSoluciones Ecuador
                      </p>
                      <div className="flex items-center justify-center space-x-2 text-gray-700 animate-fade-in-up" style={{ animationDelay: '0.2s' }}>
                        <i className="ri-phone-line text-2xl text-teal-600"></i>
                        <a href="tel:+593978966415" className="text-lg font-semibold hover:text-teal-600 cursor-pointer transition-colors">
                          +593 978 966 415
                        </a>
                      </div>
                      <p className="text-gray-600 mt-4 leading-relaxed animate-fade-in-up" style={{ animationDelay: '0.3s' }}>
                        "Comprometido con conectar a los mejores profesionales con clientes que valoran la calidad y el servicio excepcional"
                      </p>
                    </div>
                    
                    {/* Decoración de esquinas */}
                    <div className="absolute top-2 left-2 w-8 h-8 border-t-4 border-l-4 border-yellow-400 rounded-tl-lg"></div>
                    <div className="absolute top-2 right-2 w-8 h-8 border-t-4 border-r-4 border-yellow-400 rounded-tr-lg"></div>
                    <div className="absolute bottom-2 left-2 w-8 h-8 border-b-4 border-l-4 border-yellow-400 rounded-bl-lg"></div>
                    <div className="absolute bottom-2 right-2 w-8 h-8 border-b-4 border-r-4 border-yellow-400 rounded-br-lg"></div>
                  </div>
                </div>
              </div>

              {/* Image - Técnico Profesional */}
              <div ref={technicianPhotoAnimation.elementRef} className={`rounded-xl overflow-hidden shadow-xl hover:shadow-2xl hover:scale-105 transition-all duration-500 delay-700 animate-slide-in-left ${
                technicianPhotoAnimation.isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-10'
              }`}>
                <img 
                  src="https://public.readdy.ai/ai/img_res/72b6ce92704e003ce1a23f2e9e4776e7.jpg"
                  alt="Técnico profesional"
                  className="w-full h-80 object-cover object-top hover:scale-110 transition-transform duration-500"
                />
              </div>

              {/* Foto de Técnico y Cliente Satisfechos */}
              <div ref={happyClientsAnimation.elementRef} className={`rounded-xl overflow-hidden shadow-xl hover:shadow-2xl hover:scale-105 transition-all duration-500 delay-800 animate-slide-in-left ${
                happyClientsAnimation.isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-10'
              }`}>
                <div className="relative group">
                  <img 
                    src="https://readdy.ai/api/search-image?query=elderly%20male%20technician%20in%20work%20uniform%20on%20left%20side%20and%20elderly%20female%20homeowner%20on%20right%20side%20both%20smiling%20looking%20at%20each%20other%20from%20distance%20happy%20satisfied%20after%20successful%20home%20repair%20work%20completion%20professional%20photography%20bright%20natural%20lighting%20warm%20atmosphere&width=600&height=400&seq=tech-client-elderly-happy-003&orientation=landscape"
                    alt="Técnico y cliente satisfechos"
                    className="w-full h-80 object-cover object-center group-hover:scale-110 transition-transform duration-700"
                  />
                  <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-teal-600/90 to-transparent p-4">
                    <p className="text-white text-center font-bold text-lg animate-fade-in-up">
                      ✨ Satisfacción Garantizada - Clientes Felices ✨
                    </p>
                  </div>
                  {/* Efecto de brillo al hover */}
                  <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500 animate-shimmer"></div>
                </div>
              </div>
            </div>

            {/* Right Column - Form */}
            <div ref={formAnimation.elementRef} className="lg:sticky lg:top-24">
              <div className={`bg-white rounded-2xl shadow-2xl p-8 border-t-4 border-teal-600 hover:shadow-3xl transition-all duration-1000 animate-slide-in-right ${
                formAnimation.isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-10'
              }`}>
                <h3 className="text-3xl font-bold text-gray-900 mb-2">
                  Formulario de Registro
                </h3>
                <p className="text-gray-600 mb-8">
                  Complete sus datos para formar parte de nuestra base de datos de profesionales verificados
                </p>

                <form id="registro-tecnicos" data-readdy-form onSubmit={handleSubmit} className="space-y-6">
                  {/* Datos Personales */}
                  <div>
                    <h4 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                      <i className="ri-user-line text-teal-600 mr-2"></i>
                      Datos Personales
                    </h4>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label htmlFor="nombre" className="block text-sm font-medium text-gray-700 mb-2">
                          Nombre *
                        </label>
                        <input
                          type="text"
                          id="nombre"
                          name="nombre"
                          required
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                          placeholder="Juan"
                        />
                      </div>
                      
                      <div>
                        <label htmlFor="apellido" className="block text-sm font-medium text-gray-700 mb-2">
                          Apellido *
                        </label>
                        <input
                          type="text"
                          id="apellido"
                          name="apellido"
                          required
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                          placeholder="Pérez"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Cédula */}
                  <div>
                    <label htmlFor="cedula" className="block text-sm font-medium text-gray-700 mb-2">
                      Cédula de Identidad *
                    </label>
                    <input
                      type="text"
                      id="cedula"
                      name="cedula"
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                      placeholder="1234567890"
                    />
                  </div>

                  {/* Contacto */}
                  <div>
                    <h4 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                      <i className="ri-phone-line text-teal-600 mr-2"></i>
                      Información de Contacto
                    </h4>
                    
                    <div className="space-y-4">
                      <div>
                        <label htmlFor="telefono" className="block text-sm font-medium text-gray-700 mb-2">
                          Teléfono / WhatsApp *
                        </label>
                        <input
                          type="tel"
                          id="telefono"
                          name="telefono"
                          required
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                          placeholder="+593 999 999 999"
                        />
                      </div>
                      
                      <div>
                        <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                          Correo Electrónico *
                        </label>
                        <input
                          type="email"
                          id="email"
                          name="email"
                          required
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                          placeholder="ejemplo@correo.com"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Dirección */}
                  <div>
                    <label htmlFor="direccion" className="block text-sm font-medium text-gray-700 mb-2">
                      Dirección Completa *
                    </label>
                    <input
                      type="text"
                      id="direccion"
                      name="direccion"
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                      placeholder="Calle, número, ciudad, provincia"
                    />
                  </div>

                  {/* Ciudad */}
                  <div>
                    <label htmlFor="ciudad" className="block text-sm font-medium text-gray-700 mb-2">
                      Ciudad donde Trabaja *
                    </label>
                    <input
                      type="text"
                      id="ciudad"
                      name="ciudad"
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                      placeholder="Otavalo, Quito, Guayaquil, etc."
                    />
                  </div>

                  {/* Especialidad */}
                  <div>
                    <h4 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                      <i className="ri-tools-line text-teal-600 mr-2"></i>
                      Información Profesional
                    </h4>
                    
                    <label htmlFor="especialidad" className="block text-sm font-medium text-gray-700 mb-2">
                      Especialidad / Oficio *
                    </label>
                    <select
                      id="especialidad"
                      name="especialidad"
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm cursor-pointer"
                    >
                      <option value="">Seleccione su especialidad</option>
                      <option value="albanileria">Albañilería</option>
                      <option value="plomeria">Plomería</option>
                      <option value="cerrajeria">Cerrajería</option>
                      <option value="pintura">Pintura</option>
                      <option value="electricidad">Electricidad</option>
                      <option value="refrigeracion">Refrigeración y Aire Acondicionado</option>
                      <option value="jardineria">Jardinería</option>
                      <option value="reparaciones">Reparaciones Generales</option>
                      <option value="otro">Otro (especificar en descripción)</option>
                    </select>
                  </div>

                  {/* Años de Experiencia */}
                  <div>
                    <label htmlFor="experiencia_anos" className="block text-sm font-medium text-gray-700 mb-2">
                      Años de Experiencia *
                    </label>
                    <input
                      type="number"
                      id="experiencia_anos"
                      name="experiencia_anos"
                      required
                      min="0"
                      max="50"
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                      placeholder="5"
                    />
                  </div>

                  {/* Certificaciones */}
                  <div>
                    <label htmlFor="certificaciones" className="block text-sm font-medium text-gray-700 mb-2">
                      Certificaciones o Títulos
                    </label>
                    <input
                      type="text"
                      id="certificaciones"
                      name="certificaciones"
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                      placeholder="Ej: Certificado SECAP, Título técnico, etc."
                    />
                  </div>

                  {/* Descripción */}
                  <div>
                    <label htmlFor="descripcion_experiencia" className="block text-sm font-medium text-gray-700 mb-2">
                      Descripción de su Experiencia y Servicios *
                    </label>
                    <textarea
                      id="descripcion_experiencia"
                      name="descripcion_experiencia"
                      required
                      rows={5}
                      maxLength={500}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm resize-none"
                      placeholder="Describa su experiencia, proyectos realizados, especialidades específicas, herramientas que maneja, etc. (máximo 500 caracteres)"
                    ></textarea>
                    <p className="text-xs text-gray-500 mt-1">Máximo 500 caracteres</p>
                  </div>

                  {/* Disponibilidad */}
                  <div>
                    <label htmlFor="disponibilidad" className="block text-sm font-medium text-gray-700 mb-2">
                      Disponibilidad *
                    </label>
                    <select
                      id="disponibilidad"
                      name="disponibilidad"
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm cursor-pointer"
                    >
                      <option value="">Seleccione su disponibilidad</option>
                      <option value="tiempo_completo">Tiempo Completo</option>
                      <option value="medio_tiempo">Medio Tiempo</option>
                      <option value="fines_semana">Fines de Semana</option>
                      <option value="por_proyecto">Por Proyecto</option>
                      <option value="flexible">Flexible</option>
                    </select>
                  </div>

                  {/* Referencias */}
                  <div>
                    <label htmlFor="referencias" className="block text-sm font-medium text-gray-700 mb-2">
                      Referencias (Nombre y Teléfono)
                    </label>
                    <textarea
                      id="referencias"
                      name="referencias"
                      rows={3}
                      maxLength={500}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm resize-none"
                      placeholder="Ej: Juan Pérez - 0999999999, María López - 0988888888"
                    ></textarea>
                  </div>

                  {/* Nota Importante sobre Acuerdo Contractual */}
                  <div className="bg-amber-50 border-l-4 border-amber-500 rounded-lg p-4 animate-pulse-slow">
                    <div className="flex items-start space-x-3">
                      <i className="ri-information-line text-xl text-amber-600 mt-0.5 flex-shrink-0"></i>
                      <div>
                        <p className="text-sm font-semibold text-gray-900 mb-1">
                          Acuerdo Contractual Formalizado
                        </p>
                        <p className="text-xs text-gray-700 leading-relaxed">
                          Al unirse a nuestra red, todos los trabajos se formalizarán mediante un documento contractual que incluye: tipo de servicio, costo, condiciones, plazos y forma de pago acordados entre todas las partes.
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Submit Button */}
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full bg-teal-600 text-white py-4 rounded-lg font-semibold text-lg hover:bg-teal-700 transition-colors whitespace-nowrap cursor-pointer disabled:bg-gray-400 disabled:cursor-not-allowed hover:scale-105 transform duration-300"
                  >
                    {isSubmitting ? 'Enviando...' : 'Enviar Solicitud'}
                  </button>

                  {/* Status Messages */}
                  {formStatus === 'success' && (
                    <div className="bg-green-50 border border-green-200 text-green-800 px-4 py-3 rounded-lg animate-fade-in-up">
                      <p className="font-semibold">¡Solicitud enviada con éxito!</p>
                      <p className="text-sm">Nos pondremos en contacto con usted pronto para verificar sus datos.</p>
                    </div>
                  )}

                  {formStatus === 'error' && (
                    <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg animate-fade-in-up">
                      <p className="font-semibold">Error al enviar la solicitud</p>
                      <p className="text-sm">Por favor, verifique que todos los campos estén correctos e intente nuevamente.</p>
                    </div>
                  )}
                </form>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* WhatsApp Contact Section */}
      <section ref={whatsappAnimation.elementRef} className="py-16 bg-gradient-to-r from-green-500 to-green-600">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className={`bg-white rounded-2xl shadow-2xl p-8 md:p-12 hover:scale-105 transition-all duration-500 animate-scale-in ${
            whatsappAnimation.isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
          }`}>
            <div className="text-center mb-8">
              <div className="inline-flex items-center justify-center w-20 h-20 bg-green-500 rounded-full mb-4 hover:rotate-12 hover:scale-110 transition-all duration-300 animate-pulse">
                <i className="ri-whatsapp-line text-4xl text-white"></i>
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4 animate-fade-in-up">
                ¿Prefieres Contactarnos Directamente?
              </h2>
              <p className="text-xl text-gray-700 mb-6 animate-fade-in-up" style={{ animationDelay: '0.1s' }}>
                Estamos disponibles por WhatsApp para responder todas tus preguntas
              </p>
            </div>

            <div className="bg-green-50 border-2 border-green-500 rounded-xl p-8 mb-6 hover:shadow-lg transition-shadow duration-300 animate-fade-in-up" style={{ animationDelay: '0.2s' }}>
              <div className="text-center mb-6">
                <p className="text-2xl font-bold text-green-700 mb-2">
                  SOLICITAMOS TÉCNICOS CALIFICADOS
                </p>
                <p className="text-xl font-semibold text-green-600">
                  PARA SATISFACER NUESTRA DEMANDA
                </p>
              </div>
              
              <div className="flex flex-col items-center space-y-6">
                <div className="space-y-4 w-full max-w-md">
                  <div className="flex items-center justify-center space-x-3 bg-white rounded-lg p-4 shadow-md hover:shadow-xl hover:scale-105 transition-all duration-300">
                    <i className="ri-phone-line text-3xl text-green-600"></i>
                    <div className="text-left">
                      <a href="tel:+593978966415" className="text-2xl font-bold text-gray-900 hover:text-green-600 cursor-pointer transition-colors block">
                        +593 978 966 415
                      </a>
                      <p className="text-sm font-semibold text-gray-600">HogarSoluciones</p>
                    </div>
                  </div>
                  
                  <div className="flex items-center justify-center space-x-3 bg-white rounded-lg p-4 shadow-md hover:shadow-xl hover:scale-105 transition-all duration-300">
                    <i className="ri-phone-line text-3xl text-green-600"></i>
                    <div className="text-left">
                      <a href="tel:+584248082841" className="text-2xl font-bold text-gray-900 hover:text-green-600 cursor-pointer transition-colors block">
                        +58 424 808 2841
                      </a>
                      <p className="text-sm font-semibold text-gray-600">MILVA</p>
                    </div>
                  </div>
                </div>
                
                <p className="text-center text-gray-700 max-w-2xl leading-relaxed">
                  Conversemos sobre cómo podemos trabajar juntos para ofrecer servicios de calidad a nuestros clientes en todo Ecuador.
                </p>
                
                <div className="flex flex-col sm:flex-row gap-3">
                  <a
                    href="https://wa.me/593978966415?text=Hola%2C%20soy%20un%20t%C3%A9cnico%20profesional%20y%20me%20interesa%20unirme%20a%20HogarSoluciones%20Ecuador"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center bg-green-500 text-white px-6 py-3 rounded-lg text-base font-semibold hover:bg-green-600 hover:scale-105 transition-all duration-300 whitespace-nowrap cursor-pointer shadow-lg animate-pulse"
                  >
                    <i className="ri-whatsapp-line text-xl mr-2"></i>
                    WhatsApp HogarSoluciones
                  </a>
                  
                  <a
                    href="https://wa.me/584248082841?text=Hola%2C%20soy%20un%20t%C3%A9cnico%20profesional%20y%20me%20interesa%20unirme%20a%20HogarSoluciones%20Ecuador"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center bg-green-500 text-white px-6 py-3 rounded-lg text-base font-semibold hover:bg-green-600 hover:scale-105 transition-all duration-300 whitespace-nowrap cursor-pointer shadow-lg animate-pulse"
                    style={{ animationDelay: '0.2s' }}
                  >
                    <i className="ri-whatsapp-line text-xl mr-2"></i>
                    WhatsApp MILVA
                  </a>
                </div>
              </div>
            </div>

            <div className="text-center text-gray-600">
              <p className="text-sm">
                Horario de atención: Lunes a Domingo, 8:00 AM - 8:00 PM
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Map Section */}
      <section ref={mapAnimation.elementRef} className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className={`text-center mb-12 transition-all duration-1000 ${
            mapAnimation.isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
          }`}>
            <h2 className="text-4xl font-bold text-gray-900 mb-4 animate-fade-in-up">Encuéntrenos en Otavalo</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto mb-2 animate-fade-in-up" style={{ animationDelay: '0.1s' }}>
              Visítenos en nuestra oficina para conversar sobre oportunidades de trabajo
            </p>
            <div className="flex items-center justify-center space-x-2 text-lg text-gray-700 animate-fade-in-up" style={{ animationDelay: '0.2s' }}>
              <i className="ri-map-pin-line text-2xl text-teal-600"></i>
              <p className="font-semibold">
                CALLES JULIA MOSQUERA CON PEDRO PÉREZ, CIUDADELA YANAYACU 1RA ETAPA, OTAVALO
              </p>
            </div>
          </div>

          <div className={`transition-all duration-1000 delay-200 ${
            mapAnimation.isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
          }`}>
            <div className="rounded-2xl overflow-hidden shadow-2xl hover:shadow-3xl transition-shadow duration-500 border-4 border-teal-600 animate-scale-in">
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3989.2!2d-78.2627!3d0.2343!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zMMKwMTQnMDMuNSJOIDc4wrAxNSc0NS43Ilc!5e0!3m2!1ses!2sec!4v1234567890"
                width="100%"
                height="500"
                style={{ border: 0 }}
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="Ubicación de HogarSoluciones Ecuador en Otavalo"
              ></iframe>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-8">
              <div className="bg-teal-50 rounded-xl p-6 text-center hover:shadow-lg hover:scale-105 transition-all duration-300 animate-fade-in-up">
                <div className="w-16 h-16 bg-teal-600 rounded-full flex items-center justify-center mx-auto mb-4 animate-pulse">
                  <i className="ri-map-pin-line text-3xl text-white"></i>
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">Dirección</h3>
                <p className="text-gray-700 text-sm">
                  CALLES JULIA MOSQUERA CON PEDRO PÉREZ<br />
                  CIUDADELA YANAYACU 1RA ETAPA<br />
                  OTAVALO, ECUADOR
                </p>
              </div>

              <div className="bg-teal-50 rounded-xl p-6 text-center hover:shadow-lg hover:scale-105 transition-all duration-300 animate-fade-in-up" style={{ animationDelay: '0.1s' }}>
                <div className="w-16 h-16 bg-teal-600 rounded-full flex items-center justify-center mx-auto mb-4 animate-pulse">
                  <i className="ri-phone-line text-3xl text-white"></i>
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">Teléfonos</h3>
                <div className="space-y-3">
                  <div>
                    <a href="tel:+593978966415" className="text-teal-600 font-semibold text-lg hover:text-teal-700 cursor-pointer block">
                      +593 978 966 415
                    </a>
                    <p className="text-gray-600 text-sm font-medium">HogarSoluciones</p>
                  </div>
                  <div className="border-t border-gray-200 pt-2">
                    <a href="tel:+584248082841" className="text-teal-600 font-semibold text-lg hover:text-teal-700 cursor-pointer block">
                      +58 424 808 2841
                    </a>
                    <p className="text-gray-600 text-sm font-medium">MILVA</p>
                  </div>
                </div>
                <p className="text-gray-600 text-sm mt-2">Lunes a Domingo, 8:00 AM - 8:00 PM</p>
              </div>

              <div className="bg-teal-50 rounded-xl p-6 text-center hover:shadow-lg hover:scale-105 transition-all duration-300 animate-fade-in-up" style={{ animationDelay: '0.2s' }}>
                <div className="w-16 h-16 bg-teal-600 rounded-full flex items-center justify-center mx-auto mb-4 animate-pulse">
                  <i className="ri-mail-line text-3xl text-white"></i>
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">Email</h3>
                <a href="mailto:info@hogarsolucionesecuador.com" className="text-teal-600 font-semibold hover:text-teal-700 cursor-pointer">
                  info@hogarsolucionesecuador.com
                </a>
                <p className="text-gray-600 text-sm mt-2">Respuesta en 24 horas</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div>
              <div className="flex items-center space-x-3 mb-4">
                {/* Logo en Footer actualizado */}
                <img 
                  src="https://static.readdy.ai/image/e5ec1a966ead4ea4405a383d967bb80c/5b74a69037f50dd7137b29dc6f7b5981.png"
                  alt="HogarSoluciones Ecuador Logo"
                  className="h-12 w-auto"
                />
              </div>
              <p className="text-gray-400 mb-6 leading-relaxed">
                Técnicos especializados a su servicio, soluciones a su alcance. Conectando hogares, oficinas y locales comerciales con profesionales en todo Ecuador.
              </p>
              
              {/* Redes Sociales EXTRAGRANDES */}
              <div className="space-y-4">
                <h4 className="text-lg font-semibold mb-4">Síguenos en Redes Sociales</h4>
                <div className="flex flex-wrap gap-4">
                  <a 
                    href="https://www.facebook.com/profile.php?id=61571046803494" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="group"
                  >
                    <div className="w-16 h-16 bg-blue-600 rounded-full flex items-center justify-center hover:bg-blue-700 hover:scale-110 transition-all duration-300 cursor-pointer shadow-lg">
                      <i className="ri-facebook-fill text-3xl text-white"></i>
                    </div>
                    <p className="text-xs text-center mt-2 text-gray-400 group-hover:text-white transition-colors">Facebook</p>
                  </a>
                  
                  <a 
                    href="https://www.instagram.com/hogarsolucionesecuador/" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="group"
                  >
                    <div className="w-16 h-16 bg-gradient-to-br from-purple-600 via-pink-600 to-orange-500 rounded-full flex items-center justify-center hover:scale-110 transition-all duration-300 cursor-pointer shadow-lg">
                      <i className="ri-instagram-fill text-3xl text-white"></i>
                    </div>
                    <p className="text-xs text-center mt-2 text-gray-400 group-hover:text-white transition-colors">Instagram</p>
                  </a>
                  
                  <a 
                    href="https://wa.me/593978966415" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="group"
                  >
                    <div className="w-16 h-16 bg-green-500 rounded-full flex items-center justify-center hover:bg-green-600 hover:scale-110 transition-all duration-300 cursor-pointer shadow-lg border-2 border-teal-400">
                      <i className="ri-whatsapp-fill text-3xl text-white"></i>
                    </div>
                    <p className="text-xs text-center mt-2 text-gray-400 group-hover:text-white transition-colors">WhatsApp</p>
                  </a>
                  
                  <a 
                    href="https://www.tiktok.com/@hogarsolucionesecuador" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="group"
                  >
                    <div className="w-16 h-16 bg-black rounded-full flex items-center justify-center hover:bg-gray-800 hover:scale-110 transition-all duration-300 cursor-pointer shadow-lg border-2 border-teal-400">
                      <i className="ri-tiktok-fill text-3xl text-white"></i>
                    </div>
                    <p className="text-xs text-center mt-2 text-gray-400 group-hover:text-white transition-colors">TikTok</p>
                  </a>
                  
                  <a 
                    href="mailto:info@hogarsolucionesecuador.com"
                    className="group"
                  >
                    <div className="w-16 h-16 bg-red-600 rounded-full flex items-center justify-center hover:bg-red-700 hover:scale-110 transition-all duration-300 cursor-pointer shadow-lg">
                      <i className="ri-mail-fill text-3xl text-white"></i>
                    </div>
                    <p className="text-xs text-center mt-2 text-gray-400 group-hover:text-white transition-colors">Email</p>
                  </a>
                </div>
              </div>
            </div>
            
            <div>
              <h4 className="text-lg font-semibold mb-4">Servicios</h4>
              <ul className="space-y-2">
                <li><a href="/#servicios" className="text-gray-400 hover:text-white cursor-pointer">Albañilería</a></li>
                <li><a href="/#servicios" className="text-gray-400 hover:text-white cursor-pointer">Plomería</a></li>
                <li><a href="/#servicios" className="text-gray-400 hover:text-white cursor-pointer">Cerrajería</a></li>
                <li><a href="/#servicios" className="text-gray-400 hover:text-white cursor-pointer">Pintura</a></li>
                <li><a href="/#servicios" className="text-gray-400 hover:text-white cursor-pointer">Electricidad</a></li>
                <li><a href="/#servicios" className="text-gray-400 hover:text-white cursor-pointer">Refrigeración y A/C</a></li>
                <li><a href="/#servicios" className="text-gray-400 hover:text-white cursor-pointer">Jardinería</a></li>
              </ul>
            </div>
            
            <div>
              <h4 className="text-lg font-semibold mb-4">Enlaces</h4>
              <ul className="space-y-2">
                <li><Link to="/" className="text-gray-400 hover:text-white cursor-pointer">Inicio</Link></li>
                <li><a href="/#servicios" className="text-gray-400 hover:text-white cursor-pointer">Servicios</a></li>
                <li><a href="/#como-funciona" className="text-gray-400 hover:text-white cursor-pointer">Cómo Funciona</a></li>
                <li><Link to="/contacto" className="text-gray-400 hover:text-white cursor-pointer">Únete como Técnico</Link></li>
                <li><a href="/#solicitar" className="text-gray-400 hover:text-white cursor-pointer">Solicitar Servicio</a></li>
              </ul>
            </div>
            
            <div>
              <h4 className="text-lg font-semibold mb-4">Contacto</h4>
              <ul className="space-y-2">
                <li className="text-gray-400">📧 info@hogarsolucionesecuador.com</li>
                <li>
                  <a href="tel:+593978966415" className="text-gray-400 hover:text-white cursor-pointer">
                    📱 +593 978 966 415 - GERMÁN
                  </a>
                </li>
                <li>
                  <a href="tel:+584248082841" className="text-gray-400 hover:text-white cursor-pointer">
                    📱 +58 424 808 2841 - MILVA
                  </a>
                </li>
                <li className="text-gray-400">📍 Otavalo, Ecuador</li>
              </ul>
            </div>
          </div>
          
          <div className="border-t border-gray-800 mt-12 pt-8 flex flex-col md:flex-row justify-between items-center">
            <p className="text-gray-400 text-sm">
              © 2024 HogarSoluciones Ecuador. Todos los derechos reservados.
            </p>
            <a href="https://readdy.ai/?origin=logo" className="text-gray-400 hover:text-white text-sm cursor-pointer">
              Powered by Readdy
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
}
